import { useEffect, useState } from "react";
import { apiFetch } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";

type HubUser = {
  id: string;
  email: string;
  role: "hub_admin" | "hub_operator";
  is_enabled: boolean;
};

export function AdminHubUsers() {
  const [users, setUsers] = useState<HubUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<HubUser["role"]>("hub_operator");
  const [query, setQuery] = useState("");

  async function refresh(opts?: { silent?: boolean }) {
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ users: HubUser[] }>("/api/admin/hub-users");
      setUsers(data.users);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }

  async function createUser() {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ user: HubUser }>("/api/admin/hub-users", {
        method: "POST",
        body: { email, role }
      });
      setUsers((prev) => [data.user, ...prev]);
      setEmail("");
      setRole("hub_operator");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function setEnabled(id: string, is_enabled: boolean) {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ user: HubUser }>(`/api/admin/hub-users/${id}`, {
        method: "PATCH",
        body: { is_enabled }
      });
      setUsers((prev) => prev.map((u) => (u.id === id ? data.user : u)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    let last = 0;
    function maybeRefresh() {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - last < 15_000) return;
      last = now;
      void refresh({ silent: true });
    }
    window.addEventListener("focus", maybeRefresh);
    document.addEventListener("visibilitychange", maybeRefresh);
    return () => {
      window.removeEventListener("focus", maybeRefresh);
      document.removeEventListener("visibilitychange", maybeRefresh);
    };
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = users.filter((u) => {
    if (!q) return true;
    return u.email.toLowerCase().includes(q) || u.id.toLowerCase().includes(q) || u.role.toLowerCase().includes(q);
  });

  return (
    <div>
      <PageLayout
        title="Hub users"
        description="Operator accounts for Hub administration."
      >
        {error && <ErrorState detail={error} />}
        {loading && <LoadingState />}

        <div className="card" style={{ marginTop: 12 }}>
          <div className="toolbar">
            <div className="toolbarGroup" style={{ flex: "1 1 380px" }}>
              <input
                className="input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search users (email, id, role)…"
                aria-label="Search hub users"
              />
            </div>
          </div>
          <div className="help" style={{ marginTop: 8 }}>
            Showing <b>{filtered.length}</b> of <b>{users.length}</b>.
          </div>
        </div>

        <div className="card tableWrap" style={{ marginTop: 12 }}>
          <table className="table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th style={{ width: 200 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div style={{ fontWeight: 800 }}>{u.email}</div>
                    <div className="muted mono" style={{ fontSize: 12 }}>
                      {u.id}
                    </div>
                  </td>
                  <td>
                    <span className="pill">{u.role}</span>
                  </td>
                  <td>
                    <span className={u.is_enabled ? "pill pillOk" : "pill pillBad"}>{u.is_enabled ? "enabled" : "disabled"}</span>
                  </td>
                  <td>
                    <div className="actions" style={{ justifyContent: "flex-start" }}>
                      <button onClick={() => void setEnabled(u.id, !u.is_enabled)} disabled={loading} className="btn" type="button">
                        {u.is_enabled ? "Disable" : "Enable"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && !error && filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted" style={{ padding: 12 }}>
                    No users match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="card" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>Create hub user</div>
          <div className="help" style={{ marginBottom: 12 }}>
            Sends an invite email for a hub role.
          </div>

          <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
            <label className="field">
              <span className="label">Email</span>
              <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
            </label>
            <label className="field">
              <span className="label">Role</span>
              <select className="select" value={role} onChange={(e) => setRole(e.target.value as HubUser["role"])}>
                <option value="hub_operator">hub_operator</option>
                <option value="hub_admin">hub_admin</option>
              </select>
            </label>
          </div>

          <div className="actions">
            <button
              className="btn"
              type="button"
              disabled={loading}
              onClick={() => {
                setEmail("");
                setRole("hub_operator");
              }}
            >
              Clear
            </button>
            <button onClick={() => void createUser()} disabled={loading || email.trim() === ""} className="btn btnPrimary" type="button">
              Create user
            </button>
          </div>
        </div>
      </PageLayout>
    </div>
  );
}

